import { TFunction } from 'i18next';
import moment from 'moment';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { useCurrentlyViewedUser, useLocalize } from '@treams/treams-hooks';
import { ConversationIcon, DialogueIcon, LibraryIcon, TargetIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Breakpoint, Subheading, tokens } from '@treams/treams-ui';

import JourneyListItem, {
	IEvaluationSubmissionV2,
	ILearningEvent,
	JourneyItem,
} from './JourneyListItem';

interface IKakapoObject {
	createdOnDateTimeOffsetUtc: Date;
}

interface INodeObject {
	createdAt: Date;
}

const isKakapoObject = (item: IKakapoObject | INodeObject): item is IKakapoObject => {
	return (item as IKakapoObject).createdOnDateTimeOffsetUtc !== undefined;
};

export const createdTimestampFromJourneyItem = (item: JourneyItem): Date => {
	if (isKakapoObject(item)) {
		return item.createdOnDateTimeOffsetUtc;
	}
	return item.createdAt;
};
enum JourneyItemType {
	EvaluationForm,
	Goal,
	FeedbackEvent,
	EvaluationSubmission,
	LearningEvent,
}

const getJourneyItemType = (item: JourneyItem): JourneyItemType => {
	const match = item as any;

	if ((match as IEvaluationSubmissionV2).employee) {
		return JourneyItemType.EvaluationSubmission;
	}

	if ((match as ILearningEvent).learningType) {
		return JourneyItemType.LearningEvent;
	}

	return JourneyItemType.Goal;
};

const journeyItemFactory = {
	create({
		item,
		user,
		path,
		t,
		localize,
	}: {
		item: JourneyItem;
		user: graphql.User;
		path: string;
		t: TFunction;
		localize: (items: graphql.LocalizedString) => { text: string };
	}): ReactNode {
		if ('__typename' in item) {
			if (item.__typename === 'FeedbackRequest') {
				return (
					<JourneyListItem
						createdAt={item.createdAt}
						key={item.id}
						icon={<DialogueIcon />}
						to={`/users/${user.id}/feedback/${item.id}?return=/users/${user.id}/journey`}
						title={item.title || `${t('journey.list.item.label.feedbackEvent')}`}
						description={t('journey.item.decription.feedback', {
							timestamp: moment(item.createdAt).format('L'),
							resultsGiven: '',
						})}
					/>
				);
			}
		}

		switch (getJourneyItemType(item)) {
			case JourneyItemType.EvaluationSubmission: {
				return (
					<JourneyListItem
						createdAt={createdTimestampFromJourneyItem(item)}
						key={item.id}
						icon={<ConversationIcon />}
						to={`/users/${user.id}/conversations/${item.id}?return=/users/${user.id}/journey`}
						title={t('journey.list.item.label.evaluationForm')}
						description={
							localize((item as any as IEvaluationSubmissionV2).evaluationForm.title).text
						}
					/>
				);
			}

			case JourneyItemType.LearningEvent: {
				const learningEvent = item as ILearningEvent;
				return (
					<JourneyListItem
						createdAt={createdTimestampFromJourneyItem(item)}
						key={item.id}
						icon={<LibraryIcon />}
						to={`/users/${user.id}/journey/learning/${item.id}`}
						title={`${t('journey.list.item.label.learningEvent')}`}
						description={localize(learningEvent.title).text}
					/>
				);
			}

			default: {
				const goal = item as graphql.Goal;
				return (
					<JourneyListItem
						createdAt={createdTimestampFromJourneyItem(item)}
						key={item.id}
						icon={<TargetIcon />}
						to={`/users/${user.id}/goals/${item.id}?return=/users/${user.id}/journey`}
						title={t('journey.list.item.label.goal')}
						description={localize(goal.title).text}
						checklistItems={goal.checklistItems}
					/>
				);
			}
		}
	},
};

interface Props {
	month: string;
	items: JourneyItem[];
	nextMonthItemCount: number;
}

function drawSquigglyLine(
	canvas: HTMLCanvasElement,
	color: string,
	itemCount: number,
	nextItemCount: number,
) {
	const ctx = canvas.getContext('2d')!;
	ctx.fillStyle = color;
	ctx.strokeStyle = color;
	ctx.setLineDash([5, 4]);
	ctx.beginPath();

	const multiplier = itemCount % 3 === 0 ? 1 : (itemCount % 3) * 0.33;
	const multiplierNext = nextItemCount % 3 === 0 ? 1 : (nextItemCount % 3) * 0.33;

	const maxUsableWidth = multiplier * ctx.canvas.width;
	const maxUsableWidthNext = multiplierNext * ctx.canvas.width;
	ctx.moveTo(maxUsableWidth / 2, 0);

	ctx.bezierCurveTo(
		maxUsableWidth / 2 - 30,
		70,
		maxUsableWidthNext / 2 + 30,
		30,
		maxUsableWidthNext / 2,
		100,
	);
	ctx.stroke();
}

const Canvas = styled.canvas`
	width: 100%;
	height: 100px;
`;

const Month = styled.div`
	margin-bottom: ${tokens.gap.list}px;
`;

const Items = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${tokens.gap.group}px;
	align-items: stretch;

	@media screen and (min-width: ${Breakpoint.Small}) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media screen and (min-width: ${Breakpoint.Large}) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
`;

export default function JourneyListMonth({
	month,
	items,
	nextMonthItemCount,
}: Props): ReactElement {
	const { t } = useTranslation();
	const { currentlyViewedUser } = useCurrentlyViewedUser();
	const ref = useRef<HTMLCanvasElement>(null);
	const { path } = useRouteMatch();

	useEffect(() => {
		if (nextMonthItemCount === 0) {
			return;
		}
		const canvas = ref.current;
		drawSquigglyLine(canvas!, tokens.text.secondary, items.length, nextMonthItemCount);
	}, [items.length, nextMonthItemCount]);

	const { localize } = useLocalize();

	return (
		<>
			<Month>
				<Subheading>{moment(month, 'YYYY-MM').format('MMMM YYYY')}</Subheading>
			</Month>

			<Items>
				{items.map((item) =>
					journeyItemFactory.create({
						user: currentlyViewedUser,
						item,
						t,
						path,
						localize,
					}),
				)}
			</Items>
			{nextMonthItemCount > 0 && <Canvas width={800} height={100} ref={ref} />}
		</>
	);
}
